import {
  Badge,
  chakra,
  Flex,
  FlexProps,
  Icon,
  type PopoverRootProps,
  Portal,
  Text,
} from '@chakra-ui/react'
import { ChevronDown, PhoneCall } from 'lucide-react'
import React, { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
} from '~/components/ui/popover'
import { useAnalytics } from '~/hooks/useAnalytics'
import { ContactNumber } from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'
import { ZIndex } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'

import {
  CALL_CONTACT_BUTTON_CLASS_NAME,
  CallContactButtonMenuVariants,
  useCallContactButtonMenuStyles,
  useCallContactButtonStyles,
} from './componentStyles/CallContactButton.style'
import { normalizeWhitespace } from './utils/stringUtils'

const MENU_ITEM_SELECTOR = '[role="menuitem"]'

type InteractOutsideEvent = Parameters<
  NonNullable<PopoverRootProps['onInteractOutside']>
>[0]

interface CallContactButtonMenuProps {
  id: string
  labelledBy: string
  contactNumbers: ContactNumber[]
  onStartCall: (number: ContactNumber) => void
  onClose: () => void
}
const CallContactButtonMenu = ({
  id,
  labelledBy,
  contactNumbers,
  onStartCall,
  onClose,
}: CallContactButtonMenuProps) => {
  const { t } = useTranslation()
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const handleStartCall = (number: ContactNumber) => {
    onStartCall(number)
    track(WebPhoneAnalyticsEvents.SearchDropdownItemClicked)
  }

  const variant =
    contactNumbers.length > 5
      ? CallContactButtonMenuVariants.Scrollable
      : CallContactButtonMenuVariants.Fixed

  const styles = useCallContactButtonMenuStyles({ variant })

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: styles.container?.flexDirection,
    gap: styles.container?.gap,
  }

  const itemContainerProps = {
    alignItems: styles.itemContainer?.alignItems,
    gap: styles.itemContainer?.gap,
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR),
    )
    const focusedIndex = items.indexOf(document.activeElement as HTMLElement)
    const lastIndex = items.length - 1

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        items[focusedIndex >= lastIndex ? 0 : focusedIndex + 1]?.focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        items[focusedIndex <= 0 ? lastIndex : focusedIndex - 1]?.focus()
        break
      case 'Home':
        e.preventDefault()
        items[0]?.focus()
        break
      case 'End':
        e.preventDefault()
        items[lastIndex]?.focus()
        break
      case 'Escape':
      case 'Tab':
        e.preventDefault()
        e.stopPropagation()
        onClose()
        break
    }
  }

  return (
    <Flex
      id={id}
      role="menu"
      aria-labelledby={labelledBy}
      css={styles.container}
      {...containerProps}
      onKeyDown={handleKeyDown}
    >
      {contactNumbers.map((contact) => {
        return (
          <Flex
            as="button"
            role="menuitem"
            tabIndex={-1}
            key={contact.id}
            onClick={() => handleStartCall(contact)}
            css={styles.itemContainer}
            {...itemContainerProps}
          >
            <Text css={styles.itemText}>
              {t('callContactButton.callNumberWithLabel', {
                number: formatPhoneNumber(contact.number),
                label: toNounCasing(contact.label),
              })}
            </Text>
            {contact.isPrimary && (
              <Badge css={styles.itemBadge}>{t('contactItems.primary')}</Badge>
            )}
          </Flex>
        )
      })}
    </Flex>
  )
}

interface CallContactButtonProps extends FlexProps {
  contactName?: string
  defaultContactNumber: ContactNumber
  otherContactNumbers?: ContactNumber[]
  onStartCall: (number: ContactNumber) => void
  isRecentDialed?: boolean
}
export const CallContactButton = ({
  contactName,
  defaultContactNumber,
  otherContactNumbers,
  onStartCall,
  isRecentDialed,
  ...rest
}: CallContactButtonProps) => {
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuContentRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const menuButtonId = useId()

  const closeMenuAndRestoreFocus = () => {
    setMenuOpen(false)
    menuButtonRef.current?.focus()
  }

  const handleMenuButtonClick = () => {
    setMenuOpen((prev) => !prev)
  }

  const handleMenuButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && !menuOpen) {
      e.preventDefault()
      setMenuOpen(true)
    }
  }

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const handlePrimaryStartCall = (number: ContactNumber) => {
    onStartCall(number)
    if (isRecentDialed) {
      track(WebPhoneAnalyticsEvents.RecentlyDialedStartCallClicked)
    } else if (otherContactNumbers && otherContactNumbers.length > 1) {
      track(WebPhoneAnalyticsEvents.SearchStartCallWithOptionsClicked)
    } else {
      track(WebPhoneAnalyticsEvents.SearchStartCallClicked)
    }
  }

  const formattedDefaultNumber = normalizeWhitespace(
    formatPhoneNumber(defaultContactNumber.number),
  )
  const labelName = normalizeWhitespace(contactName ?? '')
  const startCallAriaLabel = labelName
    ? t('callContactButton.startCallAriaLabel', {
        name: labelName,
        number: formattedDefaultNumber,
      })
    : t('callContactButton.startCallNumberAriaLabel', {
        number: formattedDefaultNumber,
      })

  const styles = useCallContactButtonStyles()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    display: styles.container?.display,
    gap: styles.container?.gap,
  }

  const callButtonProps = {
    justifyContent: styles.callButton?.justifyContent,
    alignItems: styles.callButton?.alignItems,
    gap: styles.callButton?.gap,
    borderRadius: styles.callButton?.borderRadius,
  }

  const menuButtonProps = {
    justifyContent: styles.menuButton?.justifyContent,
    alignItems: styles.menuButton?.alignItems,
  }

  const menuButtonIconContainerProps = {
    alignItems: styles.menuButtonIconContainer?.alignItems,
    justifyContent: styles.menuButtonIconContainer?.justifyContent,
  }

  return (
    <Popover
      open={menuOpen}
      onOpenChange={({ open }: { open: boolean }) => setMenuOpen(open)}
      onInteractOutside={(e: InteractOutsideEvent) => {
        // The menu button toggles the menu itself; dismissing here would reopen it.
        if (menuButtonRef.current?.contains(e.detail.target as Node)) {
          e.preventDefault()
        }
      }}
      initialFocusEl={() =>
        menuContentRef.current?.querySelector<HTMLElement>(
          MENU_ITEM_SELECTOR,
        ) ?? null
      }
    >
      <Flex
        className={CALL_CONTACT_BUTTON_CLASS_NAME}
        data-menu-open={menuOpen ? '' : undefined}
        css={styles.container}
        {...containerProps}
        {...rest}
      >
        <PopoverAnchor>
          <Flex
            as="button"
            aria-label={startCallAriaLabel}
            onClick={() => handlePrimaryStartCall(defaultContactNumber)}
            css={styles.callButton}
            {...callButtonProps}
          >
            <Icon css={styles.callButtonIcon} aria-hidden="true">
              <PhoneCall size="100%" />
            </Icon>
            <Text css={styles.callButtonText}>
              {t('callContactButton.startCall')}
            </Text>
          </Flex>
        </PopoverAnchor>
        {otherContactNumbers && otherContactNumbers.length > 1 && (
          <chakra.button
            type="button"
            display="flex"
            ref={menuButtonRef}
            id={menuButtonId}
            aria-label={t('callContactButton.otherNumbersAriaLabel', {
              name: labelName || formattedDefaultNumber,
            })}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? menuId : undefined}
            css={styles.menuButton}
            onClick={handleMenuButtonClick}
            onKeyDown={handleMenuButtonKeyDown}
            {...menuButtonProps}
          >
            <Flex
              css={styles.menuButtonIconContainer}
              {...menuButtonIconContainerProps}
            >
              <Icon
                as={ChevronDown}
                css={styles.menuButtonIcon}
                aria-hidden="true"
              />
            </Flex>
            <Flex zIndex={ZIndex.popover}>
              <Portal>
                <PopoverContent
                  ref={menuContentRef}
                  width="fit-content"
                  bg="transparent"
                >
                  <PopoverBody minWidth="fit-content" padding="0">
                    <CallContactButtonMenu
                      id={menuId}
                      labelledBy={menuButtonId}
                      contactNumbers={otherContactNumbers}
                      onStartCall={onStartCall}
                      onClose={closeMenuAndRestoreFocus}
                    />
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Flex>
          </chakra.button>
        )}
      </Flex>
    </Popover>
  )
}
